import axios from 'axios';
import { StaffSignUp, Auth, Schedule, PatientSignUpRequestDTO, PatientEnroll, Patient } from '@types'
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import { PatientFhirSend } from 'store/types/PatientFhirSend';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URI || 'http://localhost:8080',
  // baseURL: 'http://localhost:8080',
  // withCredentials: true
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = camelcaseKeys(response.data, { deep: true });
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.request.use((config) => {
  if (config.data) {
    config.data = decamelizeKeys(config.data, { deep: true });
  }
  return config;
});

export const postPatientAsync = async (patientSignUp: PatientSignUpRequestDTO) => {
  return await apiClient.post('/api/patient/register', {
    ...patientSignUp,
  });
};
export const enrollPatientAsync = async (patientEnroll: PatientEnroll) => {
  return await apiClient.post('/api/patient/enroll', {
    ...patientEnroll,
  });
};


export const postStaffAsync = async (staffSignUp: StaffSignUp) => {
  return await apiClient.post('/api/staff/register', {
    ...staffSignUp,
  });
};

// 분당-베이글랩스 환자 등록 API
export const postPatientByHospitalAsync = async (hspCd: string, patientSignUp: PatientSignUpRequestDTO) => {
  return await apiClient.post(`/api/patient/patient-register`, {
    ...patientSignUp,
  });
};

export const requestCodeAsync = async (email: string) => {
  const response = await apiClient.post(
    `/api/auth/request/verify-code?email=${encodeURIComponent(email)}`
  );
  return response.data;
};

export const verifyCodeAsync = async (email: string, code: string) => {
  const response = await apiClient.post(
    `/api/auth/verify-code?email=${encodeURIComponent(email)}&code=${code}`
  );
  return response.data;
};

export const requestTokenAsync = async (email: string) => {
  const response = await apiClient.post(
    `/api/auth/request-password-reset?email=${encodeURIComponent(email)}`
  );
  return response.data;
};

export const verifyTokenAsync = async (token: string, newPassword: string) => {
  const response = await apiClient.post(
    `/api/staff/password-reset?token=${token}&newPassword=${newPassword}`
  );
  return response;
};

export const getHospitalsAsync = async () => {
  const response = await apiClient.get('/api/hospital');
  return response.data;
};

export const getDepartmentsAsync = async () => {
  const response = await apiClient.get('/api/department/all');
  return response.data;
};

export const getDepartmentsByHospitalAsync = async (hspCd: string) => {
  const response = await apiClient.get(`/api/department?hsp_cd=${hspCd}`);
  return response.data;
};

export const getPatientsAsync = async (hspCd: string) => {
  const response = await apiClient.get(`/api/patient/list?hspCd=${hspCd}`);
  return response;
};

export const loginPatientAsync = async (auth: Auth) => {
  return await apiClient.post('/api/patient/login', auth);
};

export const loginStaffAsync = async (auth: Auth) => {
  const response = await apiClient.post('/api/staff/login', auth);
  return response;
};

export const postScheduleAsync = async (schedule: Schedule) => {
  return await apiClient.post('/api/schedule/register', {
    ...schedule,
  });
};

export const getSchedulesAsync = async (hspCd: string) => {
  const response = await apiClient.get('/api/schedule/list', {
    params: { hspCd },
  });
  return response;
};

export const verifyUserAsync = async (token: string) => {
  const response = await apiClient.get('/api/auth/verify-token', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getPatientListAsync = async (hspCd: string) => {
  const response = await apiClient.get(`/api/patient/list?hspCd=${hspCd}`);
  return response;
};

export const getPatientListWithPdfAsync = async (hspCd: string) => {
  console.log('hspCd:', hspCd);
  // const response = await apiClient.get(`/api/patient/list?hspCd=${hspCd}`);
  const response = await apiClient.get(`/api/patient/list2?hspCd=${hspCd}`);
  return response;
};

export const getSaltedPatientDetailAsync = async (id: string, hspCd: string) => {
  const response = await apiClient.get(
    `/api/patient/list/${id}?hspCd=${hspCd}`
  );
  return response;
};

export const getSaltedPatientWorkoutDetail = async (
  ptcd: string,
  id: number,
  hspCd: string
) => {
  const response = await apiClient.get(
    `/api/patient/list/${ptcd}/${id}?hspCd=${hspCd}`
  );
  return response;
};

export const getPdfViewerAsync = async (hspCd: string, fileName: string) => {
  const response = await apiClient.get(`/api/salted/view-pdf/${hspCd}/${fileName}`);
  return response;
};

export const getCsvDownloadUrl = async(hspCd: string, fileName: string) => {
  const response = await apiClient.get(`/api/salted/downloadCsv/${hspCd}/${fileName}`)
  return response;
};

export const updateScheduleAsync = async (schedule: Schedule) => {

  const response = await apiClient.put('/api/schedule/update', schedule);

  return response;
};

export const getBagelLapsPatientDetailAsync= async (id: string, hspCd: string) => {
  const response = await apiClient.get(
    `/api/patient/list/${id}?hspCd=${hspCd}`
  );
  return response;
};


export const getBagelLapsWorkoutDetail = async (
  ptcd: string,
  id: number,
  hspCd: string
) => {
  const response = await apiClient.get(
    `/api/patient/list/${ptcd}/${id}?hspCd=${hspCd}`
  );
  return response;
};


export const deleteScheduleAsync = async (id: number) => {
  const response = await apiClient.delete(`/api/schedule/delete/${id}`);
  return response.data;
};

export const sendPatientFhirDocumentAsync = async (files: PatientFhirSend[]) => {
  const response = await apiClient.post(`/api/fhir/send`, files);
  return response.data;
};

const api = {
  getPatientListAsync,
  requestCodeAsync,
  verifyCodeAsync,
  requestTokenAsync,
  verifyTokenAsync,
  deleteScheduleAsync,
  updateScheduleAsync,
  getDepartmentsAsync,
  getPatientsAsync,
  verifyUserAsync,
  loginPatientAsync,
  postPatientAsync,
  loginStaffAsync,
  postStaffAsync,
  getHospitalsAsync,
  postScheduleAsync,
  getSchedulesAsync,
  getSaltedPatientDetailAsync,
  getPdfViewerAsync,
  getCsvDownloadUrl,
  getSaltedPatientWorkoutDetail,
  getDepartmentsByHospitalAsync,
  getBagelLapsPatientDetailAsync,
  getBagelLapsWorkoutDetail,
  enrollPatientAsync,
  getPatientListWithPdfAsync,
  sendPatientFhirDocumentAsync
};

export default api;
